import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, ShieldCheck, Loader2, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Public verified-review submission page. Accessed only via a single-use magic link
 * shared by a service professional after they mark an enquiry as "Responded".
 *
 * Flow:
 *   GET  /api/reviews/token/{token}   → validates the link, shows the pro's name
 *   POST /api/reviews/token/{token}   → submits the star rating + comment
 */
export default function Review() {
    const { token } = useParams();
    const [ctx, setCtx] = useState(null);          // { professional_name, professional_role, reviewer_name }
    const [error, setError] = useState(null);
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get(`/reviews/token/${token}`)
            .then((r) => setCtx(r.data))
            .catch((err) => setError(formatApiError(err.response?.data?.detail) || "This review link is invalid or has expired."));
    }, [token]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (rating < 1) return;
        setSubmitting(true);
        try {
            await api.post(`/reviews/token/${token}`, { rating, comment: comment.trim() || null });
            setDone(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Could not submit your review.");
        } finally {
            setSubmitting(false);
        }
    };

    if (error) return <ReviewError message={error} />;
    if (!ctx) return <ReviewLoading />;
    if (done) return <ReviewSuccess pro={ctx} />;

    const roleLabel = ROLE_LABELS[ctx.professional_role] || "Service professional";
    const stars = [1, 2, 3, 4, 5];
    const current = hover || rating;

    return (
        <div className="min-h-[100vh] bg-cream-50 flex flex-col" data-testid="review-page">
            <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-5 pb-8">
                <div className="max-w-md mx-auto">
                    <div className="text-[10px] uppercase tracking-widest text-gold-400 font-extrabold">Global Property Mart · Verified Review</div>
                    <h1 className="font-heading font-bold text-xl mt-1">Rate your experience</h1>
                    <p className="text-white/70 text-sm mt-1">
                        Hi {ctx.reviewer_name || "there"}, share your honest feedback about your interaction with
                        <span className="font-bold text-gold-300"> {ctx.professional_name}</span> ({roleLabel}).
                    </p>
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold uppercase tracking-widest">
                        <ShieldCheck className="w-3 h-3" /> Verified via enquiry
                    </div>
                </div>
            </div>

            <form onSubmit={onSubmit} className="flex-1 px-4 -mt-4 pb-8">
                <div className="max-w-md mx-auto bg-white rounded-2xl shadow-md border border-navy-500/10 p-5 space-y-5">
                    {/* Stars */}
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-navy-500/60 font-bold mb-2">Your rating</div>
                        <div className="flex items-center gap-2" data-testid="review-stars">
                            {stars.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setRating(s)}
                                    onMouseEnter={() => setHover(s)}
                                    onMouseLeave={() => setHover(0)}
                                    data-testid={`review-star-${s}`}
                                    aria-label={`${s} star${s > 1 ? "s" : ""}`}
                                    className="p-1 focus:outline-none"
                                >
                                    <Star
                                        className={`w-9 h-9 transition-transform ${s <= current ? "text-gold-500 fill-gold-500 scale-105" : "text-navy-500/25"}`}
                                        strokeWidth={1.5}
                                    />
                                </button>
                            ))}
                        </div>
                        <div className="text-xs text-navy-500/55 mt-1">
                            {rating === 0 && "Tap a star to rate (1 = poor, 5 = excellent). Honest feedback helps other buyers."}
                            {rating > 0 && RATING_HINT[rating]}
                        </div>
                    </div>

                    {/* Comment */}
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-navy-500/60 font-bold block mb-2">
                            Add a short comment (optional)
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={4}
                            maxLength={600}
                            data-testid="review-comment"
                            placeholder="What went well? What could be better? Please keep it honest and respectful."
                            className="w-full rounded-lg border border-navy-500/15 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-gold-500/60 resize-none"
                        />
                        <div className="text-[10px] text-navy-500/40 mt-1 text-right">{comment.length}/600</div>
                    </div>

                    <button
                        type="submit"
                        disabled={rating < 1 || submitting}
                        data-testid="review-submit"
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gold-500 text-navy-500 font-extrabold text-sm hover:bg-gold-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        {submitting ? "Submitting…" : "Submit Verified Review"}
                    </button>

                    <p className="text-[11px] text-navy-500/50 leading-relaxed text-center">
                        By submitting, you confirm this review is based on your genuine interaction. Reviews cannot
                        be edited or deleted by the professional. Inappropriate reviews may be moderated by GPM Admin.
                    </p>
                </div>
            </form>
        </div>
    );
}

const ROLE_LABELS = {
    loan_agent: "Loan Agent",
    advocate: "Advocate / Property Lawyer",
    architect: "Architect",
    interior_designer: "Interior Designer",
    civil_engineer: "Civil Engineer",
    civil_contractor: "Civil Contractor",
    property_valuer: "Property Valuer",
    renovation: "Renovation Services",
    broker: "Broker",
    builder: "Builder",
};

const RATING_HINT = {
    1: "Very poor experience — we're sorry.",
    2: "Below expectations.",
    3: "Average — nothing outstanding either way.",
    4: "Good — mostly satisfied.",
    5: "Excellent — highly recommend.",
};

function ReviewLoading() {
    return (
        <div className="min-h-[80vh] flex items-center justify-center text-navy-500/60">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading review…
        </div>
    );
}

function ReviewError({ message }) {
    return (
        <div className="min-h-[80vh] bg-cream-50 flex items-center justify-center px-4" data-testid="review-error">
            <div className="max-w-sm w-full bg-white rounded-2xl border border-navy-500/10 shadow-sm p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                    <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <div className="font-heading font-bold text-navy-500 text-lg">Review link problem</div>
                <p className="text-sm text-navy-500/65 mt-2">{message}</p>
                <Link
                    to="/"
                    className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-navy-500/25 text-navy-500 text-sm font-semibold hover:bg-navy-500/5"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to Home
                </Link>
            </div>
        </div>
    );
}

function ReviewSuccess({ pro }) {
    return (
        <div className="min-h-[80vh] bg-cream-50 flex items-center justify-center px-4" data-testid="review-success">
            <div className="max-w-sm w-full bg-white rounded-2xl border border-emerald-200 shadow-sm p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="w-9 h-9 text-emerald-600" />
                </div>
                <div className="font-heading font-bold text-navy-500 text-lg">Thanks for your honest review!</div>
                <p className="text-sm text-navy-500/65 mt-2">
                    Your verified review for <strong>{pro.professional_name}</strong> is now live on their public profile.
                </p>
                <Link
                    to={`/services/professional/${pro.professional_id}`}
                    className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gold-500 text-navy-500 text-sm font-extrabold hover:bg-gold-400"
                >
                    View their profile
                </Link>
            </div>
        </div>
    );
}
